import React, {
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Checkbox from "components/Checkbox/Checkbox";
import Input from "components/Input/Input";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { withFormik } from "formik";
import useFetchOnceEffect from "hooks/useFetchOnceEffect";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { compose } from "redux";
import {
  getCart,
  getDescriptionsByHsCodes,
  importCustomsDeclaration as importCustomsDeclarationCart,
} from "reduxFolder/reducers/cart";
import { getCustomsDeclarations } from "reduxFolder/reducers/outgoing";
import {
  getShipment,
  importCustomsDeclaration as importCustomsDeclarationShipment,
} from "reduxFolder/reducers/storage";
import {
  cartSelectCart,
  cartSelectLoading,
  customDeclarationsDescriptions,
} from "reduxFolder/selectors/cartSelectors";
import { createStructuredSelector } from "reselect";
import { ButtonsWrapper } from "styles/formStyles";
import {
  CustomDeclarationsErrorObject,
  CustomsDeclarationV2FormProps,
  ErrorsFormValues,
} from "types/customDeclaration";
import { createFieldBlurHandler } from "utils/formValidation";
import { toastResponseError } from "utils/responseMessageHelper";
import {
  AddUrlCheckboxWrapper,
  Cell,
  LastRow,
  Row,
  SaveButton,
  StyledForm,
  StyledTable,
  UrlCell,
  UrlRow,
} from "../CustomDeclarationV2.styles";
import { customDeclarationForm, withBlankRows } from "../formHelper";
import {
  getShowError,
  hasRealProductLink,
  URL_LINK_PLACEHOLDER,
} from "../helpers";
import { useFeature } from "../../../../branding/useFeature";
import CustomSelectDescription from "./CustomSelectDescription/CustomSelectDescription";
import DeclarationFileDropZone from "./DeclarationFileDropZone";
import DeleteRowButton from "./DeleteRowButton";
import { InvoiceParsedRow } from "types/api/invoiceParseJobs";

const defaultNewItem = {
  description: "",
  hs_code: "",
  quantity: "",
  value: "",
  product_link: "",
};

const CustomsDeclarationV2Form: FC<CustomsDeclarationV2FormProps> = React.memo(
  (props) => {
    const {
      t,
      values,
      errors = {},
      isSubmitting,
      handleChange,
      handleSubmit,
      setFieldValue,
      setValues,
      onCustomsDataChange,
      dirty,
      cart,
      getCart,
      isCartLoading,
      getCustomsDeclarations,
      importCustomsDeclarationCart,
      importCustomsDeclarationShipment,
      getShipment,
      isShipmentImportCustomsDeclaration,
      itemId,
      shipmentId,
      isWizardStep = false,
      readOnly = false,
      noPadding = false,
      autoFocus = false,
      hideFileImport = false,
      isShowHSCodeColumn,
      isHSCodeValidationRequired,
      getDescriptionsByHsCodes,
      descriptionsHsCodes,
      className,
      saveButtonText,
    } = props;
    const handleBlur = createFieldBlurHandler(props);
    const formRef = useRef<HTMLFormElement>(null);
    const [newItem, setNewItem] = useState(defaultNewItem);
    const [inputForFocus, setInputForFocus] = useState<number | null>(null);
    const [hsCodesOptions, setHsCodesOptions] = useState(descriptionsHsCodes);
    const [showUrlLinks, setShowUrlLinks] = useState(false);
    // Once the user manually toggles the URL column, stop auto-detecting so the
    // choice sticks even as `values` keeps changing (e.g. while adding rows).
    const userToggledUrlLinks = useRef(false);
    const itemsLength = values.length;
    const consolidation_id = isWizardStep ? cart.get("id") : itemId;
    const refetchId = shipmentId || consolidation_id;
    const invoiceParsingEnabled =
      useFeature("invoiceParsing") && !readOnly && !hideFileImport;
    const rowFieldOrder = [
      "description",
      ...(isShowHSCodeColumn ? ["hs_code"] : []),
      "quantity",
      "value",
      ...(showUrlLinks ? ["product_link"] : []),
    ];

    useFetchOnceEffect(!descriptionsHsCodes.size, getDescriptionsByHsCodes);

    useEffect(() => {
      if (userToggledUrlLinks.current) return;
      if (
        !showUrlLinks &&
        values.some((row) => hasRealProductLink(row.product_link))
      ) {
        setShowUrlLinks(true);
      }
    }, [values]);

    useEffect(() => {
      if (!showUrlLinks) return;
      values.forEach((row, index) => {
        if (isRowActive(row) && !row.product_link) {
          setFieldValue(`[${index}].product_link`, URL_LINK_PLACEHOLDER);
        }
      });
    }, [showUrlLinks, values]);

    useEffect(() => {
      if (
        formRef.current &&
        inputForFocus !== null &&
        formRef.current.elements[inputForFocus]
      ) {
        const inputElement = formRef.current.elements[
          inputForFocus
        ] as HTMLInputElement;
        inputElement.focus();
      }
    }, [formRef, inputForFocus]);

    useEffect(() => {
      onCustomsDataChange && onCustomsDataChange(values);
    }, [values]);

    useEffect(() => {
      const errorKeys = Object.keys(errors);
      if (errorKeys.length > 0) {
        const hasError = errorKeys.some(
          (key) => errors[key as keyof typeof errors] === "Duplicate row found",
        );

        if (hasError) {
          toastResponseError(
            t("error.duplicateRowCustomData"),
            "errorDuplicate",
          );
        }
      }
    }, [errors]);

    useEffect(() => {
      if (!descriptionsHsCodes.size) return;

      const options = descriptionsHsCodes.map((description: any) => {
        const descriptionEn = description.get("description");
        const descriptionRu = description.get("description_ru");

        return {
          value: descriptionEn,
          value_ru: descriptionRu,
        };
      });

      setHsCodesOptions(options);
    }, [descriptionsHsCodes]);

    const handleNewItemChange = (
      newValue: FormEvent<HTMLInputElement>,
      fieldName: string,
    ) => {
      const inputTarget = newValue.target as HTMLInputElement;
      const nextItem = {
        ...newItem,
        [fieldName]: inputTarget.value,
      };

      if (
        showUrlLinks &&
        fieldName !== "product_link" &&
        !nextItem.product_link &&
        isRowActive(nextItem)
      ) {
        nextItem.product_link = URL_LINK_PLACEHOLDER;
      }

      const addNewRow =
        nextItem.description !== "" &&
        nextItem.quantity !== "" &&
        nextItem.value !== "";

      setNewItem(addNewRow ? defaultNewItem : nextItem);
      if (addNewRow) {
        // Focus from correct input will be lost because we are adding new item to form.values
        const focusOffset = rowFieldOrder.indexOf(fieldName);
        const inputsPerRow = rowFieldOrder.length;
        if (focusOffset !== -1) {
          setInputForFocus(itemsLength * inputsPerRow + focusOffset);
        }
        setFieldValue(`[${itemsLength}]`, nextItem);
      }
    };

    // Receipt rows join the active rows, above fresh blank rows; the customer
    // reviews and saves as usual.
    const appendParsedRows = useCallback(
      (rows: InvoiceParsedRow[]) => {
        if (!rows.length) return;
        const stamp = Date.now();
        setValues(
          withBlankRows([
            ...values.filter((row) => isRowActive(row)),
            ...rows.map((row, index) => ({
              tempId: stamp + index,
              description: row.description,
              quantity: row.quantity,
              value: row.value ?? "",
              hs_code: row.hs_code ?? "",
              product_link: row.product_link ?? "",
              ...(row.country_of_origin ? { country_of_origin: row.country_of_origin } : {}),
            })),
          ]),
        );
      },
      [setValues, values],
    );

    const handleDeleteRow = (index: number) => {
      setValues(values.filter((val, i: number) => i !== index));
    };

    const handleDescriptionChange = useCallback(
      (index: number, selectedOption: string) => {
        setFieldValue(`[${index}].description`, selectedOption);

        const hsCode = descriptionsHsCodes.find(
          (option: any) => option.get("description") === selectedOption,
        );

        if (isShowHSCodeColumn && hsCode) {
          const code = hsCode.get("code");

          setFieldValue(`[${index}].hs_code`, code);

          //focus quantity input from formRef
          setTimeout(() => {
            if (formRef.current) {
              const quantityInput = formRef.current.querySelector(
                `input[name='[${index}].quantity']`,
              ) as HTMLInputElement;
              if (quantityInput) {
                quantityInput.focus();
              }
            }
          }, 0);
        }
      },
      [setValues, values, descriptionsHsCodes, isShowHSCodeColumn],
    );

    const handleFileUpload = useCallback(
      (file: File) => {
        const importCustomsDeclaration = isShipmentImportCustomsDeclaration
          ? importCustomsDeclarationShipment
          : importCustomsDeclarationCart;

        return importCustomsDeclaration({ id: consolidation_id, file })
          .then(() => {
            if (isWizardStep) {
              getCart();
            } else {
              if (isShipmentImportCustomsDeclaration) {
                getShipment(refetchId);
              } else {
                getCustomsDeclarations(consolidation_id);
              }
            }
          })
          .catch(toastResponseError);
      },
      [
        isWizardStep,
        consolidation_id,
        refetchId,
        getCart,
        getShipment,
        isShipmentImportCustomsDeclaration,
        importCustomsDeclarationCart,
        importCustomsDeclarationShipment,
      ],
    );

    const isRowActive = (row: {
      description?: string;
      hs_code?: string;
      quantity?: string | number;
      value?: any;
    }) => {
      return !!(row.description || row.hs_code || row.quantity || row.value);
    };

    return (
      <StyledForm
        onSubmit={handleSubmit}
        ref={formRef}
        $noPadding={noPadding}
        className={className}
      >
        {!readOnly && (
          <AddUrlCheckboxWrapper>
            <Checkbox
              label={t("shipping.addUrlLinks")}
              checked={showUrlLinks}
              onChange={() => {
                userToggledUrlLinks.current = true;
                setShowUrlLinks((prev) => !prev);
              }}
            />
          </AddUrlCheckboxWrapper>
        )}
        <StyledTable
          $readOnly={readOnly}
          $isWizardStep={!!isWizardStep}
          disabled={isCartLoading}
          $isShowHSCodeColumn={!!isShowHSCodeColumn}
        >
          <thead>
            <tr>
              <th>Description</th>
              {isShowHSCodeColumn && <th>HS code</th>}
              <th>{isShowHSCodeColumn ? "Q-ty" : "Quantity"}</th>
              <th>Price</th>
            </tr>
          </thead>
          <tbody>
            {values.map((row, index: number) => {
              return (
                <React.Fragment key={`${index}${row.id ? row.id : row.tempId}`}>
                  <Row>
                    <Cell
                      $invalid={getShowError(
                        values,
                        errors as ErrorsFormValues,
                        index,
                        "description",
                      )}
                      size="large"
                    >
                      <CustomSelectDescription
                        name={`[${index}].description`}
                        value={row.description ?? ""}
                        handleChange={(selectedOption: string) =>
                          handleDescriptionChange(index, selectedOption)
                        }
                        handleBlur={handleBlur}
                        initialOptions={hsCodesOptions}
                      />

                      <DeleteRowButton onClick={() => handleDeleteRow(index)} />
                    </Cell>
                    {isShowHSCodeColumn && (
                      <Cell
                        $invalid={getShowError(
                          values,
                          errors as ErrorsFormValues,
                          index,
                          "hs_code",
                          1,
                        )}
                      >
                        <Input
                          name={`[${index}].hs_code`}
                          value={row.hs_code}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </Cell>
                    )}
                    <Cell
                      $invalid={getShowError(
                        values,
                        errors as ErrorsFormValues,
                        index,
                        "quantity",
                        1,
                      )}
                    >
                      <Input
                        name={`[${index}].quantity`}
                        value={row.quantity}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Cell>
                    <Cell
                      $invalid={getShowError(
                        values,
                        errors as ErrorsFormValues,
                        index,
                        "value",
                        0.01,
                      )}
                    >
                      <Input
                        name={`[${index}].value`}
                        value={row.value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Cell>
                  </Row>
                  {showUrlLinks && (
                    <UrlRow>
                      <UrlCell
                        $isShowHSCodeColumn={!!isShowHSCodeColumn}
                        colSpan={isShowHSCodeColumn ? 4 : 3}
                      >
                        <Input
                          name={`[${index}].product_link`}
                          value={row.product_link ?? ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </UrlCell>
                    </UrlRow>
                  )}
                </React.Fragment>
              );
            })}
            {!readOnly && (
              <>
                <LastRow>
                  <Cell>
                    <Input
                      autoFocus={autoFocus}
                      name={`[${itemsLength}].description`}
                      value={newItem.description}
                      onChange={(newValue) =>
                        handleNewItemChange(newValue, "description")
                      }
                    />
                  </Cell>
                  {isShowHSCodeColumn && (
                    <Cell>
                      <Input
                        name={`[${itemsLength}].hs_code`}
                        value={newItem.hs_code}
                        onChange={(newValue) =>
                          handleNewItemChange(newValue, "hs_code")
                        }
                      />
                    </Cell>
                  )}
                  <Cell>
                    <Input
                      name={`[${itemsLength}].quantity`}
                      value={newItem.quantity}
                      onChange={(newValue) =>
                        handleNewItemChange(newValue, "quantity")
                      }
                    />
                  </Cell>
                  <Cell>
                    <Input
                      name={`[${itemsLength}].value`}
                      value={newItem.value}
                      onChange={(newValue) =>
                        handleNewItemChange(newValue, "value")
                      }
                    />
                  </Cell>
                </LastRow>
                {showUrlLinks && (
                  <UrlRow>
                    <UrlCell
                      $isShowHSCodeColumn={!!isShowHSCodeColumn}
                      colSpan={isShowHSCodeColumn ? 4 : 3}
                    >
                      <Input
                        name={`[${itemsLength}].product_link`}
                        value={newItem.product_link}
                        onChange={(newValue) =>
                          handleNewItemChange(newValue, "product_link")
                        }
                      />
                    </UrlCell>
                  </UrlRow>
                )}
              </>
            )}
          </tbody>
        </StyledTable>
        {!readOnly && (
          <ButtonsWrapper>
            {!hideFileImport && (
              <DeclarationFileDropZone
                onImportFile={handleFileUpload}
                onRows={appendParsedRows}
                receiptsEnabled={invoiceParsingEnabled}
              />
            )}
            <SaveButton
              isLoading={isSubmitting}
              disabled={isSubmitting || (!isWizardStep && !dirty)}
              color={ButtonColor.Primary}
              type={ButtonType.Submit}
              variant={ButtonVariant.Filled}
              $alignCenter={hideFileImport}
            >
              {saveButtonText ? saveButtonText : t("common.save")}
            </SaveButton>
          </ButtonsWrapper>
        )}
      </StyledForm>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    cart: cartSelectCart,
    isCartLoading: cartSelectLoading,
    descriptionsHsCodes: customDeclarationsDescriptions,
  }),
  {
    importCustomsDeclarationCart,
    importCustomsDeclarationShipment,
    getCart,
    getCustomsDeclarations,
    getShipment,
    getDescriptionsByHsCodes,
  },
);

const enhance = compose(
  withConnect,
  withTranslation("common"),
  withFormik(customDeclarationForm),
);

export default enhance(CustomsDeclarationV2Form);
